import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Form,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  BoxArrowRight,
  Bell,
  Search,
  CircleFill,
  PersonXFill,
  EnvelopePlus,
  Check2Circle,
  PencilSquare,
  ArrowRepeat,
  KeyFill,
  ArrowLeftRight,
  ExclamationTriangleFill,
  Copy,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";
import ICON_DASH from "../img/menu-dashboard.png";
import ICON_REPORT from "../img/menu-reporting.png";
import ICON_LEARN from "../img/menu-learn.png";
import ICON_COURSES from "../img/menu-courses.png";
import ILLU_REGISTER from "../img/name-card.png";
import ILLU_COMPARE from "../img/exchange.png";
import ILLU_TRACK from "../img/search.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_TG_LIST = `${API_BASE}/getTGByList`;
const ENDPOINT_RESEND = `${API_BASE}/adminResendActivation`;
const ENDPOINT_UPDATE_STATUS = `${API_BASE}/updateUserStatus`;
const ENDPOINT_UPDATE_USER = `${API_BASE}/updateUser`;
const ENDPOINT_UPDATE_PASSWORD = `${API_BASE}/updateUserPassword`;
const ENDPOINT_TG_CREATE_CYCLE = `${API_BASE}/tgCreateCycle`;

const USER_STATUS_OPTIONS = [
  { id: 1, label: "New" },
  { id: 5, label: "Active" },
  { id: 6, label: "Inactive" },
];

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getAuth() {
  const user = safeParse(localStorage.getItem("user"), {});
  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    "";
  const userId = user.user_id ?? user.id ?? "";

  const accountType = user.account_type || user.accountType || "";

  const isAdmin =
    user.isAdmin ??
    user.is_admin ??
    accountType === "admin" ??
    user.role === "admin" ??
    user.role_id === 1 ??
    false;

  return { token, userId, isAdmin: !!isAdmin };
}

function parseDateFlexible(v) {
  if (!v && v !== 0) return null;
  if (v instanceof Date) {
    const d = new Date(v.getFullYear(), v.getMonth(), v.getDate());
    d.setHours(23, 59, 59, 999);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof v === "number") {
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    d.setHours(23, 59, 59, 999);
    return d;
  }

  const s = String(v).trim();
  if (!s) return null;

  if (/^\d{4}$/.test(s)) {
    const year = Number(s);
    const d = new Date(year, 11, 31);
    d.setHours(23, 59, 59, 999);
    return isNaN(d.getTime()) ? null : d;
  }

  const slashParts = s.split("/");
  if (slashParts.length === 3) {
    let [p1, p2, p3] = slashParts;
    if (p1.length === 4) {
      const d = new Date(Number(p1), Number(p2) - 1, Number(p3));
      d.setHours(23, 59, 59, 999);
      return isNaN(d.getTime()) ? null : d;
    } else {
      const d = new Date(Number(p3), Number(p2) - 1, Number(p1));
      d.setHours(23, 59, 59, 999);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  const dashParts = s.split("-");
  if (dashParts.length === 3) {
    let [p1, p2, p3] = dashParts;
    let d;
    if (p1.length === 4) {
      d = new Date(Number(p1), Number(p2) - 1, Number(p3));
    } else {
      d = new Date(Number(p3), Number(p2) - 1, Number(p1));
    }
    d.setHours(23, 59, 59, 999);
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  return d;
}

function toInputDate(value) {
  const d = parseDateFlexible(value);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function daysUntil(date) {
  if (!date) return null;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isExpired(expiryStr) {
  if (!expiryStr) return false;

  const d = parseDateFlexible(expiryStr);
  if (!d) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cmp = new Date(d);
  cmp.setHours(0, 0, 0, 0);

  return cmp <= today;
}

function formatDisplayDate(value) {
  const d = parseDateFlexible(value);
  if (!d) return value || "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function MenuPill({ img, label, onClick }) {
  return (
    <div
      className="menu-pill"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) =>
        e.key === "Enter" || e.key === " " ? onClick?.() : null
      }
    >
      <div className="menu-pill__icon">
        <img src={img} alt={label} />
      </div>
      <div className="menu-pill__label">{label}</div>
    </div>
  );
}

function FeatureCard({ img, title, desc, cta, onClick, disabled = false }) {
  return (
    <div
      className="feature-card"
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
        transition: "0.2s ease",
      }}
    >
      <div className="feature-card__img">
        <img
          src={img}
          alt={title}
          style={{
            filter: disabled ? "grayscale(100%)" : "none",
          }}
        />
      </div>

      <div
        className="feature-card__title"
        style={{
          color: disabled ? "#9CA3AF" : undefined,
        }}
      >
        {title}
      </div>

      <div
        className="feature-card__desc"
        style={{
          color: disabled ? "#9CA3AF" : undefined,
        }}
      >
        {desc}
      </div>

      <Button
        className="feature-card__btn"
        onClick={onClick}
        disabled={disabled}
        style={{
          background: disabled ? "#E5E7EB" : undefined,
          borderColor: disabled ? "#E5E7EB" : undefined,
          color: disabled ? "#9CA3AF" : undefined,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {cta}
      </Button>
    </div>
  );
}

function Pill({ text, tone = "gray" }) {
  const styles = {
    base: {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: 20,
      fontWeight: 600,
      fontSize: 12,
      lineHeight: 1,
      whiteSpace: "nowrap",
    },
    green: { background: "rgba(34,197,94,0.16)", color: "#166534" },
    yellow: { background: "rgba(250,204,21,0.28)", color: "#92400e" },
    gray: { background: "rgba(107,114,128,0.2)", color: "#111827" },
  };
  const toneStyle =
    tone === "green"
      ? styles.green
      : tone === "yellow"
        ? styles.yellow
        : styles.gray;
  return <span style={{ ...styles.base, ...toneStyle }}>{text}</span>;
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase().trim();
  if (s === "active") return <Pill text="Active" tone="green" />;
  if (s === "new") return <Pill text="New" tone="yellow" />;
  if (s === "inactive") return <Pill text="Inactive" tone="gray" />;
  return <Pill text={status || "-"} tone="gray" />;
}

function ExpiryDot({ expiryStr }) {
  const exp = parseDateFlexible(expiryStr);
  if (!exp) return null;
  const d = daysUntil(exp);
  if (d === null) return null;
  if (d <= 30)
    return (
      <CircleFill
        color="#dc2626"
        size={10}
        className="me-1"
        title="Expiring within 1 month"
      />
    );
  if (d <= 90)
    return (
      <CircleFill
        color="#facc15"
        size={10}
        className="me-1"
        title="Expiring within 3 months"
      />
    );
  return null;
}

function TGListModal({ show, onHide }) {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [emailSendingId, setEmailSendingId] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    user: null,
    selectedStatusId: "",
  });
  const [statusSaving, setStatusSaving] = useState(false);

  const [editModal, setEditModal] = useState({
    open: false,
    user: null,
    form: {
      username: "",
      firstName: "",
      lastName: "",
      countryCode: "",
      phoneNo: "",
      tgExpiryDate: "",
      badgeNo: "",
      saveLimit: 0,
      reason: "dataamend",
    },
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [pwdModal, setPwdModal] = useState({
    open: false,
    user: null,
    password: "",
    confirm: "",
  });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const [sortConfig, setSortConfig] = useState(null);
  const [reason, setReason] = useState("dataamend");

  const [confirmEditModal, setConfirmEditModal] = useState({
    open: false,
    message: "",
    body: null,
  });

  const closeConfirmEditModal = () => {
    setConfirmEditModal({
      open: false,
      message: "",
      body: null,
    });
  };

  const performSaveEdit = async (body) => {
    if (!editModal.user || !body) return;

    try {
      const { token } = getAuth();
      setEditSaving(true);
      setEditError("");

      const oldExpiryInput = toInputDate(editModal.user?.tg_expiry_date || "");
      const newExpiryInput = toInputDate(body?.tgExpiryDate || "");
      const expiryChanged =
        oldExpiryInput && newExpiryInput && oldExpiryInput !== newExpiryInput;

      const { data } = await axios.post(ENDPOINT_UPDATE_USER, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!data?.success) {
        setEditError(data?.message || "Failed to update user.");
        setEditSaving(false);
        return;
      }

      // create cycle only when expiry date changed
      if (expiryChanged) {
        try {
          const cycleRes = await axios.post(
            ENDPOINT_TG_CREATE_CYCLE,
            { userId: body.userId },
            {
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              timeout: 20000,
            },
          );

          if (!cycleRes?.data?.success) {
            console.warn(
              "Cycle creation failed:",
              cycleRes?.data?.message || "Unknown error",
            );
          }
        } catch (cycleErr) {
          console.error("Create cycle error:", cycleErr);
        }
      }

      const updater = (arr) =>
        arr.map((r) =>
          r.user_id === editModal.user.user_id
            ? {
                ...r,
                username: body.username,
                first_name: body.firstName,
                last_name: body.lastName,
                country_code: body.countryCode,
                phone_number: body.phoneNo,
                tg_expiry_date: body.tgExpiryDate,
                badge_no: body.badgeNo,
                saveLimit: body.saveLimit,
                reason: body.reason,
              }
            : r,
        );

      setRows((prev) => updater(prev));
      setFiltered((prev) => updater(prev));

      alert(
        expiryChanged
          ? "User updated successfully and cycle created."
          : "User updated successfully.",
      );

      closeConfirmEditModal();
      closeEditModal();
    } catch (e) {
      console.error(e);
      setEditError("Server error while updating user. Please try again later.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmSaveEdit = async () => {
    await performSaveEdit(confirmEditModal.body);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { token, userId, isAdmin } = getAuth();

      const params = {
        type: "list",
        isAdmin: isAdmin ? 1 : 0,
        userId,
      };

      const { data } = await axios.get(ENDPOINT_TG_LIST, {
        params,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data?.data)) {
        setRows(data.data);
        setFiltered(data.data);
      } else {
        setRows([]);
        setFiltered([]);
        setErr(data?.message || "Failed to load data.");
      }
    } catch (e) {
      console.error(e);
      setErr("Server error. Please try again later.");
      setRows([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchRows();
      setQ("");
      setPage(1);
    }
  }, [show, fetchRows]);

  useEffect(() => {
    if (!q) {
      setFiltered(rows);
      setPage(1);
      return;
    }
    const qq = q.toLowerCase();
    const f = rows.filter((r) => {
      const fullName = `${r.first_name || ""} ${r.last_name || ""}`.trim();
      return (
        String(r.username || "")
          .toLowerCase()
          .includes(qq) ||
        fullName.toLowerCase().includes(qq) ||
        String(r.badge_no || "")
          .toLowerCase()
          .includes(qq) ||
        String(r.phone_number || "")
          .toLowerCase()
          .includes(qq) ||
        String(r.tg_expiry_date || "")
          .toLowerCase()
          .includes(qq)
      );
    });
    setFiltered(f);
    setPage(1);
  }, [q, rows]);

  const sortedFiltered = useMemo(() => {
    const arr = [...filtered];
    if (!sortConfig) return arr;

    const { key, direction } = sortConfig;
    const dir = direction === "desc" ? -1 : 1;

    return arr.sort((a, b) => {
      let aVal;
      let bVal;

      if (key === "badge_no") {
        const aStr = String(a.badge_no || "").trim();
        const bStr = String(b.badge_no || "").trim();
        const aNum = aStr ? parseInt(aStr, 10) : Number.POSITIVE_INFINITY;
        const bNum = bStr ? parseInt(bStr, 10) : Number.POSITIVE_INFINITY;
        aVal = isNaN(aNum) ? Number.POSITIVE_INFINITY : aNum;
        bVal = isNaN(bNum) ? Number.POSITIVE_INFINITY : bNum;
      } else if (key === "tg_expiry_date") {
        const aDate = parseDateFlexible(a.tg_expiry_date);
        const bDate = parseDateFlexible(b.tg_expiry_date);
        aVal = aDate ? aDate.getTime() : Number.POSITIVE_INFINITY;
        bVal = bDate ? bDate.getTime() : Number.POSITIVE_INFINITY;
      } else if (key === "isVerified") {
        aVal = Number(a.isVerified) === 1 ? 1 : 0;
        bVal = Number(b.isVerified) === 1 ? 1 : 0;
      } else if (key === "status_name") {
        const order = { new: 1, active: 2, inactive: 3 };
        const getRank = (row) => {
          const raw = (row.status_name || "").toString().toLowerCase().trim();
          if (order[raw]) return order[raw];
          const num = Number(row.status || row.status_id);
          return Number.isNaN(num) ? 999 : num;
        };
        aVal = getRank(a);
        bVal = getRank(b);
      } else {
        return 0;
      }

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [filtered, sortConfig]);

  const totalPages = Math.ceil(sortedFiltered.length / rowsPerPage) || 1;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedFiltered.length);
  const currentRows = sortedFiltered.slice(startIndex, endIndex);

  function formatName(name) {
    if (!name || name === "-") return name || "-";
    return String(name)
      .toLowerCase()
      .split(/\s+/)
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ""))
      .join(" ");
  }

  function VerifiedPill({ value }) {
    const isVerified = Number(value) === 1;

    const styles = {
      base: {
        display: "inline-block",
        padding: "6px 12px",
        borderRadius: 20,
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1,
        whiteSpace: "nowrap",
        textTransform: "capitalize",
      },
      green: { background: "rgba(34,197,94,0.16)", color: "#166534" },
      red: { background: "rgba(239,68,68,0.18)", color: "#991b1b" },
    };

    return (
      <span
        style={{
          ...styles.base,
          ...(isVerified ? styles.green : styles.red),
        }}
      >
        {isVerified ? "Verified" : "Unverified"}
      </span>
    );
  }

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <span
          className="text-muted ms-1"
          style={{ fontSize: 10, opacity: 0.5 }}
        ></span>
      );
    }
    return (
      <span className="ms-1" style={{ fontSize: 10 }}>
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const renderExpiryWarningIcon = (row) => {
    const tg = parseDateFlexible(row.tg_expiry_date);
    const nonRaw = row.nonLicenceExpiryDate;
    const non = parseDateFlexible(nonRaw);

    if (nonRaw === null || nonRaw === undefined) {
      return (
        <ExclamationTriangleFill
          size={16}
          color="#dc2626"
          className="me-1"
          title="No STB licence expiry date found in TMIS Learning."
        />
      );
    }

    if (non && (!tg || !isSameDay(tg, non))) {
      const tooltipText = nonRaw
        ? `Latest STB Licence Expiry: ${formatDisplayDate(nonRaw)}`
        : "Licence expiry in TMIS Learning is different.";
      return (
        <ExclamationTriangleFill
          size={16}
          color="#facc15"
          className="me-1"
          title={tooltipText}
        />
      );
    }

    return null;
  };

  const openEditModal = (row) => {
    setEditError("");
    setEditModal({
      open: true,
      user: row,
      form: {
        username: row.username || "",
        firstName: row.first_name || "",
        lastName: row.last_name || "",
        countryCode: row.country_code || "",
        phoneNo: row.phone_number || "",
        tgExpiryDate: toInputDate(row.tg_expiry_date),
        badgeNo: row.badge_no || "",
        saveLimit:
          Number(row.saveLimit) === 1 ||
          row.saveLimit === true ||
          String(row.saveLimit).toLowerCase() === "on"
            ? 1
            : 0,
        reason: reason,
      },
    });
  }; // add save Limit

  const closeEditModal = () => {
    setEditModal({
      open: false,
      user: null,
      form: {
        username: "",
        firstName: "",
        lastName: "",
        countryCode: "",
        phoneNo: "",
        tgExpiryDate: "",
        badgeNo: "",
        saveLimit: 0,
        reason: "dataamend",
      },
    });
  };

  const handleEditChange = (field, value) => {
    setEditModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value,
      },
    }));
  };

  const handleSwapNames = () => {
    setEditModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        firstName: prev.form.lastName,
        lastName: prev.form.firstName,
      },
    }));
  };

  const handleSaveEdit = () => {
    if (!editModal.user) return;

    const {
      username,
      firstName,
      lastName,
      countryCode,
      phoneNo,
      tgExpiryDate,
      badgeNo,
      saveLimit,
    } = editModal.form;

    if (!username.trim()) {
      setEditError("Username is required.");
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setEditError("First name and family name are required.");
      return;
    }

    const phoneDigits = phoneNo.replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length < 8) {
      setEditError("Phone number must contain at least 8 digits.");
      return;
    }

    const badgeTrim = badgeNo.trim();
    if (badgeTrim && !/^\d{4}$/.test(badgeTrim)) {
      setEditError("Badge number must be exactly 4 digits (0–9).");
      return;
    }

    setEditError("");

    const body = {
      userId: editModal.user.user_id,
      username: username.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      countryCode: countryCode.trim(),
      phoneNo: phoneNo.trim(),
      tgExpiryDate,
      badgeNo: badgeTrim,
      saveLimit: Number(saveLimit) === 1 ? 1 : 0,
      reason: reason,
    };

    const oldExpiryRaw = editModal.user?.tg_expiry_date || "";
    const oldExpiry = formatDisplayDate(oldExpiryRaw) || "not set";
    const newExpiry = formatDisplayDate(tgExpiryDate) || "not set";

    let confirmMessage = (
      <>Are you sure you want to update this tourist guide's details?</>
    );

    if (oldExpiry !== newExpiry) {
      confirmMessage = (
        <>
          Are you sure you want to change the expiry date from{" "}
          <strong>{oldExpiry}</strong> to <strong>{newExpiry}</strong>?
        </>
      );
    }

    setConfirmEditModal({
      open: true,
      message: confirmMessage,
      body,
    });
  }; // saveLimit Implement

  const openPwdModal = (row) => {
    setPwdError("");
    setPwdModal({
      open: true,
      user: row,
      password: "",
      confirm: "",
    });
  };

  const closePwdModal = () => {
    setPwdModal((prev) => ({
      ...prev,
      open: false,
      user: null,
      password: "",
      confirm: "",
    }));
  };

  const handleSavePassword = async () => {
    if (!pwdModal.user) return;

    if (!pwdModal.password || !pwdModal.confirm) {
      setPwdError("Please enter and confirm the new password.");
      return;
    }
    if (pwdModal.password !== pwdModal.confirm) {
      setPwdError("Password and confirm password do not match.");
      return;
    }

    try {
      const { token } = getAuth();
      setPwdSaving(true);
      setPwdError("");

      const body = {
        userId: pwdModal.user.user_id,
        password: pwdModal.password,
      };

      const { data } = await axios.post(ENDPOINT_UPDATE_PASSWORD, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!data?.success) {
        setPwdError(data?.message || "Failed to reset password.");
        setPwdSaving(false);
        return;
      }

      alert("Password has been reset successfully.");
      closePwdModal();
    } catch (e) {
      console.error(e);
      setPwdError(
        "Server error while resetting password. Please try again later.",
      );
    } finally {
      setPwdSaving(false);
    }
  };

  const handleSendEmailGuide = async (row) => {
    try {
      const { token } = getAuth();
      setEmailSendingId(row.user_id);

      const { data } = await axios.post(
        ENDPOINT_RESEND,
        { userId: row.user_id },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 20000,
        },
      );

      if (data?.success) {
        alert("Verification email has been sent successfully.");
      } else {
        alert(data?.message || "Failed to send verification email.");
      }
    } catch (e) {
      console.error(e);
      alert("Server error while sending email. Please try again later.");
    } finally {
      setEmailSendingId(null);
    }
  };

  const openChangeStatusModal = (row) => {
    let currentId = row.status ?? row.status_id ?? null;

    if (!currentId && row.status_name) {
      const found = USER_STATUS_OPTIONS.find(
        (opt) =>
          opt.label.toLowerCase() === row.status_name.toLowerCase().trim(),
      );
      if (found) currentId = found.id;
    }

    setStatusModal({
      open: true,
      user: row,
      selectedStatusId: currentId ? String(currentId) : "",
    });
  };

  const closeChangeStatusModal = () => {
    setStatusModal({
      open: false,
      user: null,
      selectedStatusId: "",
    });
  };

  const handleSaveStatus = async () => {
    if (!statusModal.user || !statusModal.selectedStatusId) {
      alert("Please select a status.");
      return;
    }

    try {
      const { token } = getAuth();
      setStatusSaving(true);

      const body = {
        statusId: statusModal.selectedStatusId,
        userId: statusModal.user.user_id,
      };

      const { data } = await axios.post(ENDPOINT_UPDATE_STATUS, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!data?.success) {
        alert(data?.message || "Failed to update status.");
        setStatusSaving(false);
        return;
      }

      const selectedOpt = USER_STATUS_OPTIONS.find(
        (opt) => String(opt.id) === String(statusModal.selectedStatusId),
      );

      const updater = (arr) =>
        arr.map((r) =>
          r.user_id === statusModal.user.user_id
            ? {
                ...r,
                status: Number(statusModal.selectedStatusId),
                status_name: selectedOpt?.label || r.status_name,
              }
            : r,
        );

      setRows((prev) => updater(prev));
      setFiltered((prev) => updater(prev));

      alert("Status updated successfully.");
      closeChangeStatusModal();
    } catch (e) {
      console.error(e);
      alert("Server error while updating status. Please try again later.");
    } finally {
      setStatusSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;

    navigator.clipboard.writeText(text);

    // optional feedback
    setCopiedUsername(text);

    setTimeout(() => {
      setCopiedUsername("");
    }, 1500);
  };

  const [copiedUsername, setCopiedUsername] = useState("");

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="modal-xxl"
        centered
        backdrop="static"
        className="custom-font"
      >
        <Modal.Header closeButton>
          <Modal.Title>Tourist Guides</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            className="d-flex justify-content-between align-items-center mb-3"
            style={{ gap: 12 }}
          >
            <div className="fw-semibold">
              Registered Tourist Guides under TMIS
            </div>
            <InputGroup style={{ maxWidth: 360 }}>
              <InputGroup.Text>
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by name, username, badge, phone, or date…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" role="status" />
            </div>
          ) : err ? (
            <div className="alert alert-danger mb-0">{err}</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead style={{ whiteSpace: "nowrap" }}>
                    <tr>
                      <th style={{ minWidth: 200 }}>Username</th>
                      <th style={{ minWidth: 220 }}>Family Name</th>
                      <th style={{ minWidth: 220 }}>First Name</th>
                      <th style={{ minWidth: 160 }}>Phone</th>
                      <th
                        style={{ minWidth: 140, cursor: "pointer" }}
                        onClick={() => handleSort("badge_no")}
                      >
                        Badge No
                        {renderSortIndicator("badge_no")}
                      </th>
                      <th
                        style={{ minWidth: 190, cursor: "pointer" }}
                        onClick={() => handleSort("tg_expiry_date")}
                      >
                        TG Expiry Date
                        {renderSortIndicator("tg_expiry_date")}
                      </th>
                      <th
                        style={{ minWidth: 140, cursor: "pointer" }}
                        onClick={() => handleSort("status_name")}
                      >
                        Status
                        {renderSortIndicator("status_name")}
                      </th>
                      <th
                        style={{ minWidth: 140, cursor: "pointer" }}
                        onClick={() => handleSort("isVerified")}
                      >
                        Verified
                        {renderSortIndicator("isVerified")}
                      </th>
                      <th style={{ minWidth: 220 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length ? (
                      currentRows.map((r) => {
                        const phone = `${r.country_code || ""} ${
                          r.phone_number || ""
                        }`.trim();
                        const isVerified = Number(r.isVerified) === 1;
                        const expired = isExpired(r.tg_expiry_date);

                        return (
                          <tr
                            key={r.user_id}
                            className={expired ? "tmis-row-expired" : ""}
                          >
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <ExpiryDot expiryStr={r.tg_expiry_date} />

                                <span>
                                  {r.username || "-"}
                                </span>

                                {r.username && (
                                  <Copy
                                    size={10}
                                    style={{
                                      cursor: "pointer",
                                      color:
                                        copiedUsername === r.username
                                          ? "#22c55e"
                                          : "#6B7280",
                                    }}
                                    title="Copy username"
                                    onClick={() => copyToClipboard(r.username)}
                                  />
                                )}
                              </div>
                            </td>
                            <td>{formatName(r.last_name) || "-"}</td>
                            <td>{formatName(r.first_name) || "-"}</td>
                            <td>{phone || "-"}</td>
                            <td>{r.badge_no || "-"}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {renderExpiryWarningIcon(r)}
                                {formatDisplayDate(r.tg_expiry_date) || "-"}
                              </div>
                            </td>
                            <td>
                              <StatusPill status={r.status_name} />
                            </td>
                            <td>
                              <VerifiedPill value={r.isVerified} />
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                {/* Edit details */}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => openEditModal(r)}
                                  title="Edit Details"
                                >
                                  <PencilSquare size={14} />
                                </Button>

                                {/* Send verification email */}
                                {!isVerified && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleSendEmailGuide(r)}
                                    disabled={emailSendingId === r.user_id}
                                    title="Send Email"
                                  >
                                    {emailSendingId === r.user_id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <EnvelopePlus size={14} />
                                    )}
                                  </Button>
                                )}

                                {/* Change Status */}
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => openChangeStatusModal(r)}
                                  title="Change Status"
                                >
                                  <ArrowRepeat size={14} />
                                </Button>

                                {/* Reset Password */}
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openPwdModal(r)}
                                  title="Reset Password"
                                >
                                  <KeyFill size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {sortedFiltered.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-2 flex-wrap">
                  <div className="text-muted" style={{ fontSize: 14 }}>
                    Showing {startIndex + 1}–{endIndex} of{" "}
                    {sortedFiltered.length} records
                  </div>
                  <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    >
                      ← Prev
                    </Button>
                    <span style={{ minWidth: 60, textAlign: "center" }}>
                      Page {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, totalPages))
                      }
                    >
                      Next →
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between flex-wrap">
          <div className="d-flex align-items-center gap-4 small text-muted flex-wrap">
            <div className="d-flex align-items-center gap-1">
              <CircleFill color="#dc2626" size={10} /> Expiring within 1 month
            </div>
            <div className="d-flex align-items-center gap-1">
              <CircleFill color="#facc15" size={10} /> Expiring within 3 months
            </div>
            <div className="d-flex align-items-center gap-2">
              <Pill text="Active" tone="green" /> Active guide
            </div>
            <div className="d-flex align-items-center gap-1">
              <ExclamationTriangleFill color="#facc15" size={20} /> Expiry Date
              Mismatch in STB List
            </div>

            <div className="d-flex align-items-center gap-1">
              <ExclamationTriangleFill color="#dc2626" size={20} /> Not Included
              in STB List
            </div>
          </div>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Status Modal */}
      <Modal
        show={statusModal.open}
        onHide={closeChangeStatusModal}
        centered
        backdrop="static"
        className="custom-font tmis-top-modal"
        backdropClassName="tmis-blur-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            User:{" "}
            <strong>
              {statusModal.user?.username || "-"} (
              {formatName(statusModal.user?.first_name)}{" "}
              {formatName(statusModal.user?.last_name)})
            </strong>
          </p>
          <Form.Group controlId="statusSelect">
            <Form.Label>Select new status</Form.Label>
            <Form.Select
              value={statusModal.selectedStatusId || ""}
              onChange={(e) =>
                setStatusModal((prev) => ({
                  ...prev,
                  selectedStatusId: e.target.value,
                }))
              }
            >
              <option value="">-- Select status --</option>
              {USER_STATUS_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeChangeStatusModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveStatus}
            disabled={statusSaving}
          >
            {statusSaving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        show={editModal.open}
        onHide={closeEditModal}
        centered
        backdrop="static"
        className="custom-font tmis-top-modal"
        backdropClassName="tmis-blur-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Tourist Guide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <div className="alert alert-danger py-2">{editError}</div>
          )}

          <Form>
            <Form.Group className="mb-3" controlId="editUsername">
              <Form.Label className="mb-0">Username</Form.Label>
              <Form.Control
                type="text"
                value={editModal.form.username}
                onChange={(e) => handleEditChange("username", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editFirstName">
              <Form.Label className="mb-0">First Name</Form.Label>
              <Form.Control
                type="text"
                value={editModal.form.firstName}
                onChange={(e) => handleEditChange("firstName", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editLastName">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>Family Name</Form.Label>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={handleSwapNames}
                  title="Swap First & Family Name"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowLeftRight size={14} />
                </Button>
              </div>
              <Form.Control
                type="text"
                value={editModal.form.lastName}
                onChange={(e) => handleEditChange("lastName", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editCountryCode">
              <Form.Label>Country Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="+65"
                value={editModal.form.countryCode}
                onChange={(e) =>
                  handleEditChange("countryCode", e.target.value)
                }
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editPhoneNo">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                value={editModal.form.phoneNo}
                onChange={(e) => handleEditChange("phoneNo", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editBadgeNo">
              <Form.Label>Badge Number</Form.Label>
              <Form.Control
                type="text"
                value={editModal.form.badgeNo}
                onChange={(e) => handleEditChange("badgeNo", e.target.value)}
                maxLength={4}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editTGExpiryDate">
              <Form.Label>TG Expiry Date</Form.Label>
              <Form.Control
                type="date"
                value={editModal.form.tgExpiryDate || ""}
                onChange={(e) =>
                  handleEditChange("tgExpiryDate", e.target.value)
                }
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editReason">
              <Form.Label>Reason</Form.Label>
              <Form.Select
                size="sm"
                value={reason || ""}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="dataamend">Data Amendment</option>
                <option value="renewal">Renewal</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="editSaveLimit">
              <div
                className="d-flex justify-content-between align-items-center px-3 py-2"
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 10,
                  background: "#f8f9fa",
                }}
              >
                <div>
                  <div className="fw-semibold">Allow No Limit</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Enable this option to remove submission limits for this user
                  </div>
                </div>

                <Form.Check
                  type="switch"
                  id="save-limit-switch"
                  checked={Number(editModal.form.saveLimit) === 1}
                  onChange={(e) =>
                    handleEditChange("saveLimit", e.target.checked ? 1 : 0)
                  }
                  label={Number(editModal.form.saveLimit) === 1 ? "On" : "Off"}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={editSaving}
          >
            {editSaving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Edit Modal */}
      {confirmEditModal.open && (
        <div className="confirm-update-overlay">
          <div className="confirm-update-box">
            <div className="confirm-update-header">
              <h5 className="mb-0">Confirm Update</h5>
              <button
                type="button"
                className="confirm-update-close"
                onClick={closeConfirmEditModal}
                disabled={editSaving}
              >
                ×
              </button>
            </div>

            <div className="confirm-update-body">
              <p className="mb-0" style={{ lineHeight: 1.7 }}>
                {confirmEditModal.message}
              </p>
            </div>

            <div className="confirm-update-footer">
              <Button
                variant="secondary"
                onClick={closeConfirmEditModal}
                disabled={editSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmSaveEdit}
                disabled={editSaving}
              >
                {editSaving ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      <Modal
        show={pwdModal.open}
        onHide={closePwdModal}
        centered
        backdrop="static"
        className="custom-font tmis-top-modal"
        backdropClassName="tmis-blur-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            User:{" "}
            <strong>
              {pwdModal.user?.username || "-"} (
              {formatName(pwdModal.user?.first_name)}{" "}
              {formatName(pwdModal.user?.last_name)})
            </strong>
          </p>
          {pwdError && (
            <div className="alert alert-danger py-2">{pwdError}</div>
          )}

          <Form>
            <Form.Group className="mb-3" controlId="pwdNew">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={pwdModal.password}
                onChange={(e) =>
                  setPwdModal((prev) => ({ ...prev, password: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-2" controlId="pwdConfirm">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={pwdModal.confirm}
                onChange={(e) =>
                  setPwdModal((prev) => ({ ...prev, confirm: e.target.value }))
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePwdModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSavePassword}
            disabled={pwdSaving}
          >
            {pwdSaving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default function TMISHome() {
  function readUserFromStorage() {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const accountType = (
        user.account_type ||
        user.accountType ||
        ""
      ).toLowerCase();
      const role = (user.role || "").toLowerCase();

      // pure admin check
      const isAdminOnly =
        user.isAdmin === true ||
        user.is_admin === true ||
        accountType === "admin" ||
        role === "admin" ||
        user.role_id === 1;

      const isStaff = accountType === "staff" || role === "staff";

      const isAdmin = isAdminOnly || isStaff;

      return {
        displayName:
          localStorage.getItem("display_name") ||
          user.display_name ||
          localStorage.getItem("username") ||
          "User Acc",
        isAdmin,
      };
    } catch {
      return {
        displayName:
          localStorage.getItem("display_name") ||
          localStorage.getItem("username") ||
          "User Acc",
        isAdmin: false,
      };
    }
  }

  const { displayName, isAdmin } = useMemo(readUserFromStorage, []);
  const navigate = useNavigate();
  const [showTG, setShowTG] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleSendEmail = () => {
    navigate("/unregister");
  };

  return (
    <div className="tmis-home custom-font">
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />

          <div className="d-flex align-items-center" style={{ gap: 20 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />

            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />

            <span>{displayName}</span>

            {/* <PersonCircle size={23} color="#1A3584" /> */}
            {/* Register Button */}
            <button
              className="btn"
              style={{
                background: "#1A3584",
                color: "#fff",
                borderRadius: "8px",
                fontWeight: 600,
                padding: "6px 14px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onClick={() => navigate("/register")}
            >
              Register New Guide
            </button>
          </div>
        </div>
      </Container>

      <div className="tmis-hero">
        <div className="container-xxl d-flex align-items-center justify-content-between">
          <div>
            <div className="tmis-hero__title">Welcome Back,</div>
            <div className="tmis-hero__subtitle">{displayName}</div>
          </div>

          {isAdmin && (
            <button className="button" onClick={() => navigate("/auditlogs")}>
              <div className="button-outer">
                <div className="button-inner">
                  <span> Audit Log</span>
                </div>
              </div>
            </button>

            //    <button
            //   type="button"
            //   className="btn btn-light border-0 d-flex align-items-center gap-2"
            //   style={{
            //     borderRadius: 999,
            //     padding: "6px 12px",
            //     fontSize: 12,
            //   }}
            //   onClick={() => navigate("/auditlogs")}
            // >
            //   <JournalText size={18} />
            //   <span>Audit Log</span>
            // </button>
          )}
        </div>
      </div>

      <div className="container-xxl">
        <div className={`tmis-tabs ${!isAdmin ? "tmis-tabs--2" : ""}`}>
          <MenuPill
            img={ICON_DASH}
            label="Dashboard"
            onClick={() => navigate("/dashboard")}
          />
          <MenuPill
            img={ICON_REPORT}
            label="Reporting"
            onClick={() => navigate("/report")}
          />

          {isAdmin && (
            <>
              <MenuPill
                img={ICON_LEARN}
                label="LEARN + DO"
                onClick={() => navigate("/learndo")}
              />
              <MenuPill img={ICON_COURSES} label="Courses" />
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <Container className="tmis-features">
          <Row className="g-4">
            <Col xs={12} md={6} lg={4}>
              <FeatureCard
                img={ILLU_REGISTER}
                title="Update Expiry Date"
                desc="Manage Expiry Date submission and keep guide records up to date."
                cta="Manage Submissions"
                onClick={() => navigate("/renewal")}
              />
            </Col>
            <Col xs={12} md={6} lg={4}>
              <FeatureCard
                img={ILLU_COMPARE}
                title="Comparison"
                desc="Compare registered tourist guides with STB Excel upload data to identify missing or unmatched records"
                cta="Compare"
                onClick={() => navigate("/comparison")}
              />
            </Col>
            <Col xs={12} md={6} lg={4}>
              <FeatureCard
                img={ILLU_TRACK}
                title="Tourist Guides"
                desc="View the registered Tourist Guides under TMIS"
                cta="View"
                onClick={() => setShowTG(true)}
              />
            </Col>
          </Row>

          <Row className="g-4 mt-2">
            <Col xs={12}>
              <div className="tmis-callout">
                <div className="tmis-callout__left">
                  <div className="tmis-callout__avatar">
                    <PersonXFill size={28} />
                  </div>
                  <div className="tmis-callout__texts">
                    <div className="tmis-callout__title">Send Email</div>
                    <div className="tmis-callout__desc">
                      Send a notification email to users who are{" "}
                      <strong>not registered under TMIS</strong>. Use this tool
                      to invite them to complete registration and keep your
                      guide records up to date.
                    </div>
                  </div>
                </div>

                <div className="tmis-callout__right">
                  <div className="tmis-steps">
                    <div className="tmis-step">
                      <Check2Circle className="tmis-step__icon" size={18} />
                      <div className="tmis-step__text">
                        <strong>Step 1:</strong> Select the list of unregistered
                        users.
                      </div>
                    </div>
                    <div className="tmis-step">
                      <Check2Circle className="tmis-step__icon" size={18} />
                      <div className="tmis-step__text">
                        <strong>Step 2:</strong> Review and confirm the email
                        recipients.
                      </div>
                    </div>
                    <div className="tmis-step">
                      <Check2Circle className="tmis-step__icon" size={18} />
                      <div className="tmis-step__text">
                        <strong>Step 3:</strong> Send the email invitation with
                        one click.
                      </div>
                    </div>
                  </div>

                  <Button
                    className="tmis-callout__btn"
                    onClick={handleSendEmail}
                    aria-label="Send Email to Unregistered Users"
                  >
                    <EnvelopePlus size={18} className="me-2" />
                    Send Email
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      )}

      <TGListModal show={showTG} onHide={() => setShowTG(false)} />
    </div>
  );
}
